import {useEffect, useRef, useState} from "react";
import {Button, Col, Form, message, Radio, Row, Space, Tag, Spin, Divider} from "antd";
import {CustomerWrapperDto} from "../../dto/CustomerWrapperDto";
import documentService from "../../service/documentService";
import {CustomerDto} from "../../dto/CustomerDto";
import {CheckCircleOutlined} from "@ant-design/icons";
import {PageStep} from "../../enums/PageStep";
import ErrorHandler from "../../util/ErrorHandler";
import {MSG_DOWNLOAD_THE_DOCUMENTS_WARN, MSG_FAILED_TO_FETCH_FORM_TEMPLATES_ERROR} from "../../util/Messages";
import './style.scss'
import {step_5_inactive_img} from '../../../AppImageConst';
import {DownloadOutlined} from '@ant-design/icons/lib';
import {RADIO_BUTTON_VALUES} from "../../util/Constants";

interface OtherDetailsPropData {
    savedCusDetailFromPreviousStep: CustomerWrapperDto
    getStepperFilledData: (data: CustomerWrapperDto) => void,
    switchStep?: () => void,
    isFromSummaryView: boolean
}


const W9_FORM = "W9_FORM";
const FATCA_FORM = "FATCA_FORM";
const OtherDetail = (props: OtherDetailsPropData) => {
    const [selectedCitizenStatus, setSelectedCitizenStatus] = useState<string>(RADIO_BUTTON_VALUES.NO);
    const [w9FormTemplateUrl, setWFormTemplateUrl] = useState<string>("");
    const [fatcaTemplateUrl, setFatcaTemplateUrl] = useState<string>("");
    const [fatcaDocumentName, setFatcaDocumentName] = useState<string>("");
    const [w9DocumentName, setW9DocumentName] = useState<string>("");
    const [isDocumentsDownloaded, setDocumentsDownloaded] = useState<boolean>(false);
    const [form] = Form.useForm();
    const btnW9Download = useRef<HTMLAnchorElement>(null)
    const btnFatcaDownload = useRef<HTMLAnchorElement>(null)
    const [isSpinnerLoading, setSpinnerLoading] = useState<boolean>(false)

    const onChangeGreenCardStatus = (evt: Event) => {
        let value = (evt.target as HTMLInputElement).value as unknown as string;
        setSelectedCitizenStatus(value)
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        let savedCusDetailFromPreviousStep = props.savedCusDetailFromPreviousStep;
        let customerMstDto = savedCusDetailFromPreviousStep.customerMstDto;

        if (null != customerMstDto.greenCardHolderStatus && null != customerMstDto.politicallyExposedStatus) {
            let greenCardHolderStatus: string = customerMstDto.greenCardHolderStatus
            form.setFieldsValue(
                {
                    politicallyExposedStatus: customerMstDto.politicallyExposedStatus,
                    greenCardHolderStatus: greenCardHolderStatus
                }
            )
            setSelectedCitizenStatus(greenCardHolderStatus)
            setDocumentsDownloaded(RADIO_BUTTON_VALUES.YES === customerMstDto.greenCardHolderStatus)
        } else {
            form.setFieldsValue(
                {
                    politicallyExposedStatus: RADIO_BUTTON_VALUES.NO,
                    greenCardHolderStatus: RADIO_BUTTON_VALUES.NO
                }
            )
        }


    }, [])

    const downloadFormTemplates = () => {
        setSpinnerLoading(true)
        documentService.fetchDocuments([W9_FORM, FATCA_FORM]).then(res => {
            if (res.data !== null) {
                let w9Obj = res.data.results[0];
                let fatcaObj = res.data.results[1];
                setWFormTemplateUrl(w9Obj.thumbB64)
                setFatcaTemplateUrl(fatcaObj.thumbB64)
                setW9DocumentName(w9Obj.originalDocumentName)
                setFatcaDocumentName(fatcaObj.originalDocumentName)

                setTimeout(() => {
                    btnW9Download.current?.click();
                    btnFatcaDownload.current?.click();
                    setDocumentsDownloaded(true);
                    setSpinnerLoading(false)
                }, 1000)
            } else {
                setSpinnerLoading(false)
                message.error(MSG_FAILED_TO_FETCH_FORM_TEMPLATES_ERROR).then();
            }
        }, (reason) => {
            setSpinnerLoading(false)
            ErrorHandler.handleError(reason);
        })
    }

    const submitStep = (value: any) => {
        if (selectedCitizenStatus != RADIO_BUTTON_VALUES.YES || isDocumentsDownloaded) {
            setSpinnerLoading(true)
            let customerWrapperDto = props.savedCusDetailFromPreviousStep;
            let customerMstDto = customerWrapperDto.customerMstDto;
            let actionStatus = null != customerMstDto.greenCardHolderStatus && null != customerMstDto.politicallyExposedStatus;
            if (checkChanges(customerMstDto, value)) {
                customerMstDto.politicallyExposedStatus = value.politicallyExposedStatus;
                customerMstDto.greenCardHolderStatus = value.greenCardHolderStatus;
                customerWrapperDto.recordStatus = actionStatus ? "UPDATE" : "SAVE";
                customerWrapperDto.isFinalStep = false;
                customerWrapperDto.currentActiveStep = PageStep.STEP_OTHER_DETAIL;
                customerWrapperDto.customerMstDto = customerMstDto;
                props.getStepperFilledData(customerWrapperDto);
                setSpinnerLoading(false)
            } else {
                if (props.switchStep) props.switchStep()
            }
        } else {
            message.warning(MSG_DOWNLOAD_THE_DOCUMENTS_WARN)
        }
    }

    const checkChanges = (prvCustomerDto: CustomerDto, curFormValues: any) => {
        if (prvCustomerDto.greenCardHolderStatus != curFormValues.greenCardHolderStatus) {
            return true;
        } else if (prvCustomerDto.politicallyExposedStatus != curFormValues.politicallyExposedStatus) {
            return true;
        }
        return false;
    }

    return (
        <Spin spinning={isSpinnerLoading} tip="Loading" size="large">
            <div>
                <p className='step_card_header'>
                    <div className="icon-container">
                        <img className='header-icon-img' src={step_5_inactive_img} />
                    </div>
                    Other Information</p>
                <Form
                    className='form-input'
                    layout={"vertical"}
                    initialValues={{remember: true}}
                    form={form}
                    onFinish={submitStep}
                    disabled={props.isFromSummaryView}
                >
                    <Row gutter={[32,0]}>
                        <Col span={24}>
                            <Form.Item style={{marginBottom: 5}} className='other_info_select'
                                       label={
                                           <>
                                               Are You a U.S Resident, U.S Citizen or Holder of U.S Permanent Resident Card (Green Card)? If
                                               "<span style={{ fontWeight: 'bold' }}>Yes</span>", Please Download and Fill the FATCA Declaration Form.
                                           </>
                                       }
                                       name='greenCardHolderStatus'
                                       rules={!props.isFromSummaryView ? [{
                                           required: true,
                                           message: 'Please select your citizenship status'
                                       }] : []}>
                                <Radio.Group className='radio_group_set_view' onChange={onChangeGreenCardStatus}>
                                    <Radio value={RADIO_BUTTON_VALUES.YES}>Yes</Radio>
                                    <Radio value={RADIO_BUTTON_VALUES.NO}>No</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>

                        <Col xxl={12} xl={12} lg={16} md={24} sm={24} xs={24} style={{marginTop: 8}}>
                                {RADIO_BUTTON_VALUES.YES === selectedCitizenStatus && !props.isFromSummaryView ?
                                    <Form.Item
                                        label="Download & Fill W9 Form and FATCA Declaration Form">
                                        <Button className='form-download-btn' onClick={downloadFormTemplates} block icon={<DownloadOutlined />}>
                                            Download W9 Form & FATCA Declaration Form
                                        </Button>
                                        <a hidden ref={btnW9Download} href={w9FormTemplateUrl}
                                           download={w9DocumentName}></a>
                                        <a hidden ref={btnFatcaDownload} href={fatcaTemplateUrl}
                                           download={fatcaDocumentName}></a>
                                    </Form.Item> : RADIO_BUTTON_VALUES.YES === selectedCitizenStatus && props.isFromSummaryView ?
                                    <Tag icon={<CheckCircleOutlined/>} color="success" style={{marginBottom: 12}}>
                                        W9 form and FATCA declaration forms downloaded
                                    </Tag> : ''
                                }
                            <Form.Item className='pep-form-item'
                                    label="Are You a Politically Exposed Person (PEP)"
                                    name='politicallyExposedStatus'
                                    rules={!props.isFromSummaryView ? [{
                                        required: true,
                                        message: 'Please select your political status'
                                    }] : []}>
                                    <Radio.Group >
                                        <Radio value={RADIO_BUTTON_VALUES.YES}>Yes</Radio>
                                        <Radio value={RADIO_BUTTON_VALUES.NO}>No</Radio>
                                    </Radio.Group>
                                </Form.Item>
                        </Col>
                    </Row>
                    {
                        !props.isFromSummaryView ?
                            <>
                                <Row>
                                    <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                                        <Divider></Divider>
                                    </Col>
                                </Row>
                                <Space style={{float: 'right'}} className='form_next_btn_col'>
                                    <Form.Item>
                                        <Button className='form_next_btn' htmlType="submit" type="primary">
                                            Next
                                        </Button>
                                    </Form.Item>
                                </Space>
                            </> : ''
                    }
                </Form>
            </div>
        </Spin>
    )

}
export default OtherDetail;

